import * as tools from '../Tools';
import { ToggleButton } from '../Components/ToggleButton';

interface ISConstructionStation {
    contact?: string;
    crdate: number;
    criticalDrain?: number;
    criticalHight?: number;
    dateEnd: string;
    dateStart: string;
    deployment?: number;
    id: number;
    pegelId?: number;
    position?: string;
    registeredUser?: number;
    remarks?: string;
    reportValue?: number;
    responsible?: string;
    talsperre?: number;
    toebOccurrence?: string;
    'upload[]'?: string; // Multiple Upload
}

export class ConstructionStation {
    private static updateUrl = '/ConstructionStation/update';
    private static editUrl = '/ConstructionStation/edit';
    private static deleteStationUrl = '/ConstructionStation/deleteStation';

    protected container: NodeListOf<HTMLElement>;
    protected dialog: JQuery<HTMLElement>;
    protected dialogData: ISConstructionStation;
    protected toggleButtons: Array<HTMLInputElement> = [];
    protected stationRow: HTMLElement;
    protected uploadButtons: NodeListOf<HTMLButtonElement>;
    protected fileUpload : HTMLInputElement = null;

    constructor() {
        this.container = document.querySelectorAll('.constructionStationContainer');
        if (!this.container.length) {
            return;
        }
        this.initDialog();
        this.container.forEach((container) => this.initToggleButton(container));
        window.addEventListener('evToggleButtonStateChange', (ev: CustomEvent) =>
            this.evToggle(ev)
        );
    }

    /**
     * Wechselt die Ansicht Liste oder nur Warnungen
     * @param ev
     */
    evToggle(ev: CustomEvent) {
        const evData: IToggleButtonStateChange = ev.detail;
        this.toggleButtons.forEach((button) => {
            if (button === evData.button) {
                const containerList: HTMLElement = button
                    .closest('.constructionStationContainer')
                    ?.querySelector('.constructionStationList');
                if (containerList) {
                    if (evData.state) {
                        containerList.classList.remove('hidden');
                    } else {
                        containerList.classList.add('hidden');
                    }
                }
            }
        });
    }

    /**
     * Initialisiert den Toggle-Button für die Ansicht
     * @param container
     */
    initToggleButton(container: HTMLElement) {
        const button = <HTMLInputElement>(
            container.querySelector('.bar .bar-toggle-button input.toggle-button')
        );
        if (button) {
            this.toggleButtons.push(button);
        }
    }

    /**
     * Wrapper für click-events
     * @param el
     * @param action
     */
    initAction(el: HTMLElement, action: Function) {
        el.addEventListener('click', () => action.call(this, el));
    }

    /**
     * Bearbeiten einer Baustelle
     * @param el
     */
    async editAction(el: HTMLElement) {
        const stationRow: HTMLElement = el.closest('div.station');

        this.dialogData = await tools.fetchJSON(
            ConstructionStation.editUrl + '/' + stationRow.dataset.id
        );
        if (this.dialogData.id == <number>(<unknown>stationRow.dataset.id)) {
            this.stationRow = stationRow;
            this.dialog.dialog('open');
        }
    }

    /**
     * Löscht eine Baustelle
     * Es wird nur das flag deleted gesetzt, nicht aus der DB gelöscht
     * @param container
     */
    async deleteAction(el: HTMLElement) {
        if (confirm('Sicher das Sie den Eintrag löschen möchten?')) {
            const stationRow: HTMLElement = el.closest('div.station');
            const result = await tools.fetchPOST(ConstructionStation.deleteStationUrl, {
                id: stationRow.dataset.id
            });
            if (result.result) {
                document
                    .querySelectorAll('div.station[data-id="' + stationRow.dataset.id + '"]')
                    .forEach((el) => {
                        el.remove();
                    });
            }
            window.tools.toast('Baustelle', 'Der Eintrag wurde gelöscht');
        }
    }

    /**
     * Baustellendaten speichern
     */
    async updateData(close = true) {
        const container = this.dialog.dialog('widget')[0];
        const data = tools.getFormData(container);
        const result = await tools.fetchPOST(ConstructionStation.updateUrl, data, true);
        if (close && parseInt(result.id)) {
            document.getElementById('worker').style.display = 'block';
            await this.closeDialog();
            location.reload();
        } else {
            return result;
        }
    }

    /**
     * Öffnet Dialog für neu erstellen oder bearbeiten
     */
    openDialog() {
        const widget = this.dialog.dialog('widget')[0];
        if (!this.dialogData) {
            widget.querySelectorAll('input.rw-date-now').forEach((el: HTMLInputElement) => {
                tools.setDateInputNow(el);
            });
            widget.querySelectorAll('input.rw-date-future').forEach((el: HTMLInputElement) => {
                let dtInterval: string = el.dataset.future;
                let future = tools.setFuture(dtInterval);
                tools.setDateInputNow(el, future);
            });
        } else {
            this.fillDialog(widget);
        }
        if (!this.fileUpload) {
            this.fileUpload = <HTMLInputElement>widget.querySelector('.field-upload');
            this.fileUpload.addEventListener('change', (ev) => this.evToggleUploadButton(ev));
            this.fileUpload.addEventListener('dragenter', () => this.fileUpload.classList.add('drag'));
            this.fileUpload.addEventListener('dragover', () => this.fileUpload.classList.add('drag'));
            this.fileUpload.addEventListener('dragleave', () => this.fileUpload.classList.remove('drag'));
            this.fileUpload.addEventListener('drop', () => this.fileUpload.classList.remove('drag'));
            this.uploadButtons = this.fileUpload.parentElement.querySelectorAll(
                'button.upload-action-button'
            );
            this.uploadButtons.forEach((el) =>
                el.addEventListener('click', (ev) => this.evUpload(el, this.fileUpload))
            );
        }
    }

    /**
     * Show/Hide upload and reset buttons
     * register buttons click
     * @param ev Event
     */
    evToggleUploadButton(ev: Event) {

        if (this.fileUpload.files.length > 0) {
            this.uploadButtons.forEach((el) => el.classList.remove('hidden'));
        } else {
            this.uploadButtons.forEach((el) => el.classList.add('hidden'));
        }
    }

    /**
     * Create Station if not exists and upload files
     *
     * @param button HTMLButtonElement
     * @param fileInput HTMLInputElement
     * @returns
     */
    async evUpload(button: HTMLButtonElement, fileInput: HTMLInputElement) {
        if (button.classList.contains('action-upload-reset')) {
            fileInput.value = '';
            fileInput.dispatchEvent(new Event('change'));
            return;
        }
        this.dialogData = await this.updateData(false);
        const widget = this.dialog.dialog('widget')[0];
        this.fillDialog(widget);
        fileInput.dispatchEvent(new Event('change'));
        window.tools.toast('Baustelle', 'Upload abgeschlossen');
    }

    /**
     * Füllt den Dialog mit Daten zur Bearbeitung
     * @param widget
     */
    fillDialog(widget: HTMLElement) {
        for (const key in this.dialogData) {
            const field: HTMLElement = widget.querySelector('[data-property="' + key + '"]');
            if (field) {
                switch (field.tagName) {
                    case 'TEXTAREA':
                        (<HTMLTextAreaElement>field).value = this.dialogData[key];
                        break;
                    case 'INPUT':
                        if ((<HTMLInputElement>field).type === 'datetime-local') {
                            // 2022-02-15 11:12:00 => 2022-02-15T11:12:00
                            let dateLocalString: string = this.dialogData[key];
                            dateLocalString = dateLocalString.replace(/\s/, 'T');
                            (<HTMLInputElement>field).value = dateLocalString;
                        } else if ((<HTMLInputElement>field).type === 'file') {
                            const oldUl = field.parentElement.parentElement.querySelectorAll('ul');
                            oldUl.forEach((el) => el.remove());
                            (<HTMLInputElement>field).value = '';
                            // uploadListe
                            if (this.dialogData[key] && this.dialogData[key].length > 0) {
                                const ul = document.createElement('ul');
                                this.dialogData[key].forEach((fileElement) => {
                                    const li = document.createElement('li');
                                    li.innerHTML =
                                        '<i class="icon-trash pointer" title="Datei löschen"></i>&nbsp;<a href="' +
                                        fileElement.target +
                                        '" target="_blanc">' +
                                        fileElement.filename +
                                        '</a>';
                                    li.addEventListener('click', () =>
                                        this.evDeleteFile(li, fileElement, this.dialogData.id)
                                    );
                                    ul.insertAdjacentElement('beforeend', li);
                                });
                                field.parentElement.insertAdjacentElement('afterend', ul);
                            }
                        } else {
                            if ((<HTMLInputElement>field).type === 'date') {
                                let dateValue = this.dialogData[key];
                                dateValue = dateValue.replace(/\s\d\d:\d\d:\d\d/,'');
                                (<HTMLInputElement>field).value = dateValue;
                            } else {
                                (<HTMLInputElement>field).value = this.dialogData[key];
                            }
                        }
                        break;
                    case 'DIV':
                        field.innerHTML = this.dialogData[key];
                        field.focus();
                        break;
                    case 'SELECT':
                        (<HTMLSelectElement>field).selectedIndex = 0;
                        Array.from((<HTMLSelectElement>field).options).forEach((option, index) => {
                            if (option.value === this.dialogData[key]) {
                                (<HTMLSelectElement>field).selectedIndex = index;
                            }
                        });
                        break;
                    default:
                    // console.log(field.tagName);
                }
            } else {
                // console.log(key);
            }
        }
        widget.focus();
    }

    /**
     * Löscht einen Dateianhang aus einer Baustelle
     * @param li
     * @param fileElement
     * @param stationId
     */
    async evDeleteFile(li: HTMLLIElement, fileElement: {}, stationId: number) {
        if (
            confirm(
                'Sicher das Sie die Datei löschen möchten? Sie kann nicht wieder hergestellt werden.'
            )
        ) {
            let postData: IDictionary = {
                id: stationId,
                fileElement: JSON.stringify(fileElement)
            };
            const result = await tools.fetchPOST('/ConstructionStation/deleteFile', postData);
            if (result.result) {
                li.remove();
            }
        }
    }

    /**
     * Schließt das Dialogfenster
     */
    closeDialog() {
        for (const key in this.dialogData) {
            this.dialogData[key] = '';
        }
        this.fillDialog(this.dialog.dialog('widget')[0]);
        this.dialogData = null;
        this.dialog.dialog('close');
    }

    /**
     * Initialisiert den Dialog zum neu erstellen und bearbeiten
     */
    initDialog() {
        this.dialog = $('#dialogConstructionStation').dialog({
            autoOpen: false,
            modal: true,
            width: 700,
            title: 'Baustelle',
            height: 720,
            maxHeight: 800,
            buttons: [
                {
                    text: 'Speichern',
                    class: 'ok',
                    click: () => this.updateData()
                },
                {
                    text: 'Abbrechen',
                    class: 'cancel',
                    click: () => this.closeDialog()
                }
            ],
            open: () => this.openDialog()
        });

        this.container.forEach((container) => {
            container
                .querySelector('.addConstructionStation')
                ?.addEventListener('click', () => this.dialog.dialog('open'));
            container
                .querySelectorAll('.row i.action-edit')
                ?.forEach((el: HTMLElement) => this.initAction(el, this.editAction));
            container
                .querySelectorAll('.row i.action-delete')
                ?.forEach((el: HTMLElement) => this.initAction(el, this.deleteAction));
        });
    }
}
